import { Component, OnInit, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { SubscriptionService } from '../../shared/core/state/subscription/subscription.service';
import { PageLoaderService } from '../../shared/util/pageloader.service';
import { Invoice, InvoiceStatus } from '../../shared/models/invoice.model';
import { finalize } from 'rxjs';
import { Payment } from '../../shared/core/state/order/order.model';

@Component({
  selector: 'black-invoice-preview',
  templateUrl: './invoice-preview.component.html',
  styleUrls: ['./invoice-preview.component.css'],
  standalone: false,
})
export class InvoicePreviewComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private subscriptionService = inject(SubscriptionService);
  private pageLoaderService = inject(PageLoaderService);

  invoice: Invoice | null = null;
  isLoading = true;
  error = false;
  isDownloading = false;

  ngOnInit(): void {
    const invoiceId = this.route.snapshot.paramMap.get('id');
    if (invoiceId) {
      this.pageLoaderService.open();
      this.subscriptionService
        .fetchInvoice(invoiceId)
        .pipe(
          finalize(() => {
            this.pageLoaderService.close();
            this.isLoading = false;
          })
        )
        .subscribe({
          next: (invoice) => {
            this.invoice = invoice;
            this.error = false;
          },
          error: () => {
            this.error = true;
            this.invoice = null;
          },
        });
    } else {
      this.isLoading = false;
      this.error = true;
    }
  }

  calculatePayable(amount: number, payments: Payment[]): number {
    if (!payments || payments.length === 0) return amount;
    const paidAmount = payments.reduce(
      (sum, payment) => sum + (payment.amount || 0),
      0
    );
    return amount - paidAmount;
  }

  isInvoicePayable(status: InvoiceStatus): boolean {
    return ![
      InvoiceStatus.CANCELLED,
      InvoiceStatus.PAID,
      InvoiceStatus.REFUNDED,
      InvoiceStatus.REFUND_REQUESTED,
    ].includes(status);
  }

  payInvoice(invoiceId: string) {
    this.router.navigate(['subscription', 'invoice', invoiceId, 'pay']);
  }

  downloadFile(fileUrl: string, fileName: string) {
    const link = document.createElement('a');
    link.href = fileUrl;
    link.download = fileName;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  downloadInvoice() {
    if (this.invoice) {
      this.isDownloading = true;
      this.subscriptionService
        .downloadInvoice(this.invoice._id)
        .pipe(
          finalize(() => {
            this.isDownloading = false;
          })
        )
        .subscribe((response) => {
          // Create a blob from the PDF stream
          const blob = new Blob([response], { type: 'application/pdf' });
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `invoice-${this.invoice?.invoiceNumber}.pdf`;
          link.click();
          window.URL.revokeObjectURL(url);
        });
    }
  }
}
